'use client'
import { formatCurrency } from '@/lib/utils'
import { SearchProductsToolResult, VespaPartResult } from '@/lib/utils/vespa'
import { Message as MessageType, useChat } from '@ai-sdk/react'
import { generateId } from 'ai'
import { usePostHog } from 'posthog-js/react'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { HiArrowUp } from 'react-icons/hi'
import { RiShareBoxLine } from 'react-icons/ri'
import Textarea from 'react-textarea-autosize'
import { useTheme } from '../lib/themeContext' // Import the theme context
import { Message } from './message'

export function Chat({
  id,
  savedMessages,
  initialCurrentPrompts,
  initialShowFeedback = false,
  initialWasPromptClicked = false, // This is used for tracking to differentiate between users who clicked on a prompt and those who only use the input field
  readOnly = false,
  initialInput = null,
  productInfo = null,
  checkoutMode = false
}: {
  readOnly: boolean
  id: string
  savedMessages?: MessageType[]
  initialCurrentPrompts?: typeof questions
  initialShowFeedback?: boolean
  initialWasPromptClicked?: boolean
  initialInput?: string | null
  productInfo?: any | null
  checkoutMode?: boolean
}) {
  const {
    primary,
    accent,
    secondary,
    questions,
    starterMessages,
    api,
    showCallButton,
    title
  } = useTheme() // Consume the theme context
  const posthog = usePostHog()

  const [showInvestorRelationsButtons, setShowInvestorRelationsButtons] =
    useState(false)
  const [initialInputSubmitted, setInitialInputSubmitted] = useState(false)
  const checkoutInitialized = useRef(false)

  const [llmCheckoutQuestion, setLlmCheckoutQuestion] = useState<string | null>(
    null
  )
  const [llmCheckoutButtons, setLlmCheckoutButtons] = useState<
    Array<{ text: string; value: string; nextActionHint?: string }>
  >([])

  const [actualInitialMessages] = useState<MessageType[]>(() => {
    if (checkoutMode && productInfo) {
      return [
        {
          role: 'assistant',
          content: `Hey there I'm ${title}! I'm here to help you find exactly what you need to get started.`,
          id: generateId()
        },
        {
          role: 'assistant',
          content: 'Think of me as your personal concierge!',
          id: generateId()
        },
        {
          role: 'assistant',
          content: 'How would you like to get started today?',
          id: generateId()
        }
      ] as MessageType[]
    }
    return (savedMessages || starterMessages || []) as MessageType[]
  })

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    error,
    reload,
    setMessages,
    append,
    status
  } = useChat({
    api: api,
    id,
    // onError: error => {
    //   console.error(error)
    //   console.log(error.message)
    //   console.log(error.cause)
    //   console.log(error.name)
    // },
    //@ts-ignore
    initialMessages: actualInitialMessages,
    onToolCall: ({ toolCall }) => {
      if (toolCall.toolName === 'searchInvestorRelations') {
        console.log(toolCall.args)
        setShowInvestorRelationsButtons(true)
      }
    },
    sendExtraMessageFields: true
  })

  // Store append in a ref to avoid dependency issues in useEffect
  const appendRef = useRef(append)
  useEffect(() => {
    appendRef.current = append
  }, [append])

  const ref = useRef<HTMLDivElement>(null)

  const [currentPrompts, setCurrentPrompts] = useState<
    { text: string; children?: any; parentText?: string; url?: string }[]
  >(!readOnly ? (checkoutMode ? [] : (initialCurrentPrompts ?? questions)) : [])
  const [showFeedback, setShowFeedback] = useState(initialShowFeedback)
  const [wasPromptClicked, setWasPromptClicked] = useState(
    initialWasPromptClicked
  )

  // State to track if thinking indicator should be visible
  const [showThinkingIndicator, setShowThinkingIndicator] = useState(false)

  // Only update chatId in localStorage when user actually interacts with the chat
  // This prevents overwriting the previous chat ID when just viewing a new chat
  // If we have savedMessages, that means the user has already interacted with this chat
  const hasUserInteracted =
    messages.length > (actualInitialMessages?.length || 0) ||
    (savedMessages && savedMessages.length > 0)

  // Log when status changes
  useEffect(() => {
    // When submission starts, show the indicator
    if (status === 'submitted') {
      setShowThinkingIndicator(true)
    }

    // When chat returns to ready state, hide the indicator
    if (status === 'ready' || status === 'error') {
      setShowThinkingIndicator(false)
    }
  }, [status])

  // Check for text content in messages to hide the indicator when streaming starts
  useEffect(() => {
    // Don't run this check during non-streaming states
    if (status !== 'streaming') return

    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.role === 'assistant') {
      // Check if there's substantial text content (at least 10 characters)
      const hasSubstantialTextContent = lastMessage.parts?.some(
        part =>
          part.type === 'text' && part.text && part.text.trim().length >= 10
      )

      // Only hide indicator when we have enough text content to display
      if (hasSubstantialTextContent) {
        setShowThinkingIndicator(false)
      }
    }
  }, [messages, status])

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: 'smooth'
      })
    }

    // Only update chat metadata in localStorage when user has actually interacted
    // This prevents mismatched data where lastChatMessage is from a new chat but chatId is from old chat
    if (hasUserInteracted) {
      localStorage.setItem('chatId', id)
      localStorage.setItem(
        'lastChatMessage',
        messages[messages.length - 1]?.content || ''
      )
      localStorage.setItem('lastChatDate', new Date().toISOString())
      localStorage.setItem('currentPrompts', JSON.stringify(currentPrompts))
      localStorage.setItem('showFeedback', JSON.stringify(showFeedback))
      localStorage.setItem('promptClicked', JSON.stringify(wasPromptClicked))
    }
  }, [
    messages,
    status,
    hasUserInteracted,
    currentPrompts,
    showFeedback,
    wasPromptClicked,
    id
  ])

  function customSubmit(event: React.FormEvent<HTMLFormElement>) {
    // Show thinking indicator immediately when form is submitted
    setShowThinkingIndicator(true)

    if (error != null) {
      setMessages(messages.slice(0, -1)) // remove last message
    }
    // Clear LLM UI state on any custom submission
    setLlmCheckoutQuestion(null)
    setLlmCheckoutButtons([])

    setShowFeedback(true)
    setShowInvestorRelationsButtons(false)
    setCurrentPrompts([])
    handleSubmit(event)
  }

  // Effect to handle automatic submission of initialInput
  useEffect(() => {
    if (
      initialInput &&
      !initialInputSubmitted &&
      !checkoutMode &&
      messages.length === actualInitialMessages.length
    ) {
      // Only submit if we haven't added any messages yet (only have initial messages)
      handleInputChange({
        target: { value: initialInput }
      } as React.ChangeEvent<HTMLTextAreaElement>)

      // Use setTimeout to ensure the input is set before submitting
      setTimeout(() => {
        const form = document.getElementById('chat-form') as HTMLFormElement
        if (form) {
          form.dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true })
          )
          setInitialInputSubmitted(true)
        }
      }, 100)
    }
  }, [
    initialInput,
    messages,
    actualInitialMessages,
    initialInputSubmitted,
    checkoutMode,
    handleInputChange
  ])

  // PARSE MESSAGES FOR LLM TOOL UI (askQuestionWithButtons, askFreeFormQuestion)
  useEffect(() => {
    const lastMessage = messages[messages.length - 1]

    if (
      lastMessage &&
      lastMessage.role === 'assistant' &&
      lastMessage.toolInvocations
    ) {
      let newQuestionCandidate: string | null = null
      let newButtonsCandidate: Array<{
        text: string
        value: string
        nextActionHint?: string
      }> | null = null
      let uiToolProcessed = false

      for (const toolInvocation of lastMessage.toolInvocations) {
        if (toolInvocation.toolName === 'askQuestionWithButtons') {
          try {
            const args =
              typeof toolInvocation.args === 'string'
                ? JSON.parse(toolInvocation.args)
                : toolInvocation.args
            if (args.questionText && args.buttons) {
              newQuestionCandidate = args.questionText
              newButtonsCandidate = args.buttons
              uiToolProcessed = true
              break
            }
          } catch (e) {
            console.error(
              'Error parsing args for askQuestionWithButtonsTool:',
              e,
              toolInvocation.args
            )
          }
        } else if (toolInvocation.toolName === 'askFreeFormQuestion') {
          try {
            const args =
              typeof toolInvocation.args === 'string'
                ? JSON.parse(toolInvocation.args)
                : toolInvocation.args
            if (args.questionText) {
              newQuestionCandidate = args.questionText
              newButtonsCandidate = [] // For free-form questions, buttons array is empty
              uiToolProcessed = true
              break
            }
          } catch (e) {
            console.error(
              'Error parsing args for askFreeFormQuestionTool:',
              e,
              toolInvocation.args
            )
          }
        }
      }

      if (uiToolProcessed) {
        if (newQuestionCandidate !== llmCheckoutQuestion) {
          setLlmCheckoutQuestion(newQuestionCandidate)
        }
        const currentButtonsString = JSON.stringify(llmCheckoutButtons)
        const newButtonsString = JSON.stringify(newButtonsCandidate || [])
        if (newButtonsString !== currentButtonsString) {
          setLlmCheckoutButtons(newButtonsCandidate || [])
        }
        if (currentPrompts.length > 0) {
          setCurrentPrompts([])
        }
      }
    }
  }, [messages, llmCheckoutQuestion, llmCheckoutButtons, currentPrompts])

  // Handler for LLM-driven checkout button clicks
  function handleLlmCheckoutButtonClick(button: {
    text: string
    value: string
    nextActionHint?: string
  }) {
    appendRef.current({
      role: 'user',
      content: button.text,
      id: generateId()
    })
    setLlmCheckoutQuestion(null)
    setLlmCheckoutButtons([])
    setCurrentPrompts([])
  }

  // Simple function to send the checkout purchase message
  const sendCheckoutPurchaseMessage = useCallback(() => {
    if (checkoutInitialized.current || !productInfo) {
      return
    }

    appendRef.current({
      role: 'user',
      content: `Get Started`,
      annotations: [
        {
          activeProduct: productInfo
        }
      ],
      id: generateId()
    })
    checkoutInitialized.current = true
  }, [productInfo])

  // Checkout initialization logic
  useEffect(() => {
    if (
      checkoutMode &&
      !checkoutInitialized.current &&
      status === 'ready' &&
      productInfo
    ) {
      const timer = setTimeout(() => {
        sendCheckoutPurchaseMessage()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [checkoutMode, status, sendCheckoutPurchaseMessage, productInfo])

  return (
    <div
      className="relative mx-auto flex size-full h-full max-w-3xl flex-1 flex-col overflow-auto md:px-12"
      style={{ borderColor: primary }}
    >
      <div
        ref={ref}
        className="relative flex h-full flex-1 flex-col gap-2.5 overflow-auto px-3 py-4"
      >
        {/* {messages.map(({ message, direction }, index) => (
          <Message key={index} message={message} direction={direction} />
        ))} */}

        {messages?.map((message, index) => (
          <React.Fragment key={message.id}>
            {(
              message.parts ||
              (Array.isArray(message.content) ? message.content : [])
            ).map(part => {
              switch (part.type) {
                // render text parts as simple text:
                case 'text':
                  if (part.text) {
                    return (
                      <Message
                        message={part.text}
                        direction={message.role === 'user' ? 'right' : 'left'}
                      />
                    )
                  } else {
                    return null
                  }

                // for tool invocations, distinguish between the tools and the state:
                case 'tool-invocation': {
                  switch (part.toolInvocation.toolName) {
                    case 'searchProducts': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return (
                            <>
                              <Message
                                direction="left"
                                message={`Here are some options available for you:`}
                              />
                              {part.toolInvocation.result.map(
                                (product: SearchProductsToolResult) => {
                                  return (
                                    <ProductCard
                                      key={product.id}
                                      product={product}
                                      onClick={() => {
                                        posthog.capture('$productCardClick', {
                                          $type: 'product',
                                          $product: product.id,
                                          $productUrl: product.productUrl,
                                          $chatId: id,
                                          $make: product.make,
                                          $model: product.model,
                                          $year: product.year,
                                          $stock: product.stockNumber
                                        })
                                      }}
                                    />
                                  )
                                }
                              )}
                            </>
                          )
                      }
                      break
                    }
                    case 'searchParts': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return part.toolInvocation.result
                            .slice(0, 3)
                            .map((part: any) => (
                              <PartCard
                                key={part.id}
                                part={part}
                                onClick={() => {
                                  posthog.capture('$productCardClick', {
                                    $type: 'part',
                                    $product: part.id,
                                    $productUrl: part.url,
                                    $chatId: id,
                                    $partNumber: part.partNumber
                                  })
                                }}
                              />
                            ))
                      }
                      break
                    }
                    case 'careerDevelopment': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return (
                            <a
                              target="_blank"
                              className="flex items-center gap-1 self-start rounded-lg bg-[#F3F3F3] p-2.5 text-sm font-semibold underline"
                              key={part.toolInvocation.toolCallId}
                              href={part.toolInvocation.result.link}
                              style={{ color: primary }}
                            >
                              {part.toolInvocation.result.text}
                              <RiShareBoxLine
                                className="h-5 w-5"
                                style={{ color: primary }}
                              />
                            </a>
                          )
                      }
                      break
                    }

                    case 'callbackRequest': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return (
                            <>
                              {showCallButton && (
                                <a
                                  href="tel:8882892273"
                                  className="flex items-center justify-center gap-1 rounded-lg p-2 text-sm text-white shadow-md"
                                  style={{ backgroundColor: accent }}
                                >
                                  <span className="font-bold">Call Us:</span>
                                  (888) 289-2273
                                </a>
                              )}
                            </>
                          )
                      }
                      break
                    }
                  }
                }
              }
            })}
          </React.Fragment>
        ))}

        {/* Render LLM Checkout Buttons */}
        {status === 'ready' && llmCheckoutButtons.length > 0 && (
          <div className="flex flex-col items-end gap-2 self-end py-2">
            {llmCheckoutButtons.map((button, index) => (
              <button
                key={`${button.text}-${index}-llm-checkout`}
                onClick={() => handleLlmCheckoutButtonClick(button)}
                className="self-end rounded-3xl border px-2.5 py-2 text-sm font-semibold shadow-md"
                style={{
                  borderColor: primary,
                  color: secondary
                }}
              >
                {button.text}
              </button>
            ))}
          </div>
        )}

        {status === 'ready' &&
          !llmCheckoutQuestion && // Only show if no LLM question
          llmCheckoutButtons.length === 0 && // Only show if no LLM buttons
          currentPrompts.map((question, index) =>
            question.url ? (
              <a
                target="_blank"
                className="flex items-center gap-1 self-start rounded-lg bg-[#F3F3F3] p-2.5 text-sm font-semibold underline"
                key={question.text}
                href={question.url}
              >
                {question.text}
                <RiShareBoxLine
                  className="h-5 w-5"
                  style={{ color: primary }}
                />
              </a>
            ) : (
              <button
                key={question.text}
                onClick={() => {
                  posthog.capture('$promptClick', {
                    $prompt: question.text,
                    $chatId: id,
                    ...(question?.parentText && {
                      $parentPrompt: question.parentText
                    }) // This is used to identify secondary prompts in reporting
                  })

                  setWasPromptClicked(true)
                  // Clear LLM UI when a standard prompt is clicked
                  setLlmCheckoutQuestion(null)
                  setLlmCheckoutButtons([])

                  if (question.url) {
                    window.open(question.url, '_blank')
                  } else {
                    setMessages(messages => [
                      ...messages,
                      {
                        content: question.text,
                        id: generateId(),
                        role: 'user'
                      }
                    ])
                    reload()
                    setCurrentPrompts(question.children || [])
                  }
                }}
                className="self-end rounded-3xl border px-2.5 py-2 text-sm font-semibold shadow-md"
                style={{
                  borderColor: primary === '#ffffff' ? '#000000' : primary,
                  color: secondary
                }}
              >
                {question.text}
              </button>
            )
          )}

        {error && (
          <>
            <div style={{ color: primary }}>An error occurred.</div>
            <button
              type="button"
              onClick={() => {
                posthog.capture('retry_click', { $chatId: id })
                // Clear LLM UI on retry
                setLlmCheckoutQuestion(null)
                setLlmCheckoutButtons([])
                return reload()
              }}
              style={{
                borderColor: primary,
                color: secondary
              }}
            >
              Retry
            </button>
          </>
        )}
        {!readOnly &&
          status === 'ready' &&
          showFeedback &&
          !llmCheckoutQuestion && // Only show if no LLM question
          llmCheckoutButtons.length === 0 && // Only show if no LLM buttons
          !showInvestorRelationsButtons && (
            <div className="flex gap-2.5 self-end">
              <button
                onClick={() => {
                  posthog.capture('$thatHelped', {
                    $chatId: id
                  })
                  // Clear LLM UI when giving feedback
                  setLlmCheckoutQuestion(null)
                  setLlmCheckoutButtons([])
                  appendRef.current({
                    content: 'That Helped',
                    id: generateId(),
                    role: 'user'
                  })
                  setShowFeedback(false)
                }}
                className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
              >
                That Helped
              </button>
              <button
                onClick={() => {
                  posthog.capture('$speakToATeamMember', {
                    $chatId: id
                  })
                  // Clear LLM UI when giving feedback
                  setLlmCheckoutQuestion(null)
                  setLlmCheckoutButtons([])
                  appendRef.current({
                    content: 'Speak to Team Member',
                    id: generateId(),
                    role: 'user'
                  })
                  setShowFeedback(false)
                }}
                className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
              >
                Speak to Team Member
              </button>
            </div>
          )}
        {/* Buttons that link to the appropriate investor relations pages */}
        {status === 'ready' && showInvestorRelationsButtons && (
          <div className="flex flex-col gap-2.5 self-end">
            {[
              {
                text: 'Stock Information',
                href: 'https://ir.titanmachinery.com/Stock_Quote'
              },
              {
                text: 'Contact Investor Relations',
                href: 'https://ir.titanmachinery.com/Contact_IR'
              },
              {
                text: 'Events & Presentations',
                href: 'https://ir.titanmachinery.com/Events_Presentations'
              }
            ].map(button => (
              <a
                key={button.text}
                href={button.href}
                target="_blank"
                className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
              >
                {button.text}
              </a>
            ))}
          </div>
        )}
        {showThinkingIndicator && <LoadingIndicator />}
      </div>
      {!readOnly && (
        <div className="sticky bottom-0 bg-white px-4 pb-12 lg:px-0">
          <form id="chat-form" onSubmit={customSubmit}>
            <div className="relative flex w-full items-center">
              <Textarea
                onChange={handleInputChange}
                value={input}
                required
                name="input"
                rows={1}
                tabIndex={0}
                placeholder={
                  llmCheckoutQuestion && llmCheckoutButtons.length === 0
                    ? 'Your answer...'
                    : 'Message'
                }
                spellCheck={false}
                className="flex max-h-60 w-full resize-none rounded-3xl border border-input py-4 pl-4 pr-14 text-base leading-5 ring-offset-background placeholder:text-muted-foreground hover:shadow-lg focus-visible:shadow-lg focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                onKeyDown={e => {
                  // Enter should submit the form
                  if (e.key === 'Enter' && !e.shiftKey) {
                    // Prevent the default action to avoid adding a new line
                    e.preventDefault()
                    posthog.capture('$sendClick', {
                      $input: input,
                      $promptClicked: wasPromptClicked ? true : false,
                      $chatId: id
                    })
                    const textarea = e.target as HTMLTextAreaElement
                    textarea.form?.requestSubmit()
                  }
                }}
              />
              <button
                onClick={() => {
                  posthog.capture('$sendClick', {
                    $input: input,
                    $promptClicked: wasPromptClicked ? true : false,
                    $chatId: id
                  })
                }}
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 text-xl"
                style={{
                  backgroundColor: primary === '#ffffff' ? accent : primary
                }}
              >
                <HiArrowUp className="text-white" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

const ProductCard = ({
  product,
  onClick
}: {
  product: SearchProductsToolResult
  onClick: () => void
}) => {
  const { primary } = useTheme() // Consume the theme context
  return (
    <a
      href={product.productUrl}
      target="_blank"
      className="flex flex-col gap-1 rounded-lg bg-[#F3F3F3] p-2.5"
      onClick={onClick}
    >
      <div className="flex gap-1.5">
        <div>
          <img className="w-24" src={product.primaryImage} alt={product.make} />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <a
              href={product.productUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="text-sm font-semibold underline">
                {[product.year, product.make, product.model]
                  .filter(Boolean)
                  .join(' ')}
              </div>
            </a>
            <a
              href={product.productUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <RiShareBoxLine className="h-5 w-5" style={{ color: primary }} />
            </a>
          </div>
          <div className="text-sm font-semibold text-[#4F4F4F]">
            {product.price ? formatCurrency(product.price) : 'Call for Pricing'}
          </div>
          <div className="text-sm font-semibold text-[#4F4F4F]">
            {product.location}
          </div>
        </div>
      </div>
      <div className="line-clamp-2 text-sm text-[#4F4F4F]">
        {product.description}
      </div>
      <div className="text-sm font-semibold text-[#4F4F4F]">
        Stock # {product.dealerStockNumber}
      </div>
    </a>
  )
}

const PartCard = ({
  part,
  onClick
}: {
  part: VespaPartResult
  onClick: () => void
}) => {
  const { primary } = useTheme() // Consume the theme context
  return (
    <a
      target="_blank"
      href={part.url}
      className="flex flex-col gap-1 rounded-lg bg-[#F3F3F3] p-2.5"
      onClick={onClick}
    >
      <div className="flex gap-1.5">
        <div>
          <img className="w-24" src={part.primaryImage} alt={part.title} />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <a href={part.url} target="_blank" rel="noopener noreferrer">
              <div className="text-sm font-semibold underline">
                {part.title}
              </div>
            </a>
            <a href={part.url} target="_blank" rel="noopener noreferrer">
              <RiShareBoxLine className="h-5 w-5" style={{ color: primary }} />
            </a>
          </div>
          {part.price ? (
            <div className="text-sm font-semibold text-[#4F4F4F]">
              {formatCurrency(part.price)}
            </div>
          ) : null}
        </div>
      </div>
      <div className="line-clamp-2 text-sm text-[#4F4F4F]">
        {part.description}
      </div>
      <div className="text-sm font-semibold text-[#4F4F4F]">
        Part # {part.partNumber}
      </div>
    </a>
  )
}

function LoadingIndicator() {
  return <div className="text-xs font-bold text-[#656C79]">Thinking...</div>
}
